import { Component } from "react";
import { OutreachExportFormatType, OutreachDataFilterType, OutreachExportType } from "../enumerations";
import { SelectDataFilter, SelectDateRangeFilter, SelectFormatFilter } from "./filters";
import GenerateBody from "./generate-body";
import OutreachService from "../../outreachService";
import { ExportNetworkOutreachModel } from "../../models/exportModels";
import { QESAlert } from "@app/shared/qes-standards";
import * as toast from "./toast";
import * as helpers from "./exportPanelBodyHelpers";
import { OutreachReportPanelBodyProps, OutreachReportPanelBodyState } from "./outreachReportPanelBodyBase";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

class OutreachDataPanelBody extends Component<OutreachReportPanelBodyProps & FeatureFlagContextProps, OutreachReportPanelBodyState> {
  private readonly exportService = new OutreachService();

  constructor(props: OutreachReportPanelBodyProps & FeatureFlagContextProps) {
    super(props);

    this.state = {
      ...this.state,
      activeExportType: props.activeExportType,
      dataFilterSelection: OutreachDataFilterType.All,
      emailOnSuccess: true,
      error: "",
      isLoading: true,
      lastQuarters: [],
      outputFormat: OutreachExportFormatType.CSV,
      reportName: helpers.reportName(`NetworkOutreach_${this.formatReportName(props.activeExportType)}`),
      selectedQuarter: ""
    }
  }

  public componentDidMount() {
    const lastQuarters: { key: string; value: string }[] = this.calculateQuarters();

    this.setState((prevState) => ({
      ...prevState,
      isLoading: false,
      lastQuarters: lastQuarters,
      selectedQuarter: history.state.state.currentQuarter ?? lastQuarters[0].key
    }));
  }

  public render() {
    return (
      <>
        <div className="row">
          {!!this.state.error && <QESAlert classNames="col-12" type="danger" persist={true}>{this.state.error}</QESAlert>}
        </div>
        <div className="row">
          {this.renderFirstColumnFilters()}
          {this.renderSecondColumnFilters()}
          {this.renderThirdColumnFilters()}
        </div>
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.isLoading}
        />
      </>
    );
  }

  private renderFirstColumnFilters() {
    return (
      <div className="col-12 col-sm-3" >
        <SelectDataFilter
          testIdPrefix="outreach-data"
          dataSelection={this.state.dataFilterSelection}
          onDataSelectionChange={this.handleDataSelectionChange}
        />
      </div>
    );
  }

  private renderSecondColumnFilters() {
    return (
      <div className="col-12 col-sm-3">
        <SelectDateRangeFilter
          testIdPrefix="outreach-data"
          selectedQuarter={this.state.selectedQuarter}
          onSelectQuarterChange={this.handleSelectQuarterChange}
          options={this.state.lastQuarters}
        />
      </div>
    );
  }

  private renderThirdColumnFilters() {
    return (
      <div className="col-12 col-sm-3">
        <SelectFormatFilter
          testIdPrefix="outreach-data"
          outputFormat={this.state.outputFormat}
          onOutputFormatChange={this.handleOutputFormatChange} />
      </div>
    );
  }

  private handleDataSelectionChange = (dataSelection: OutreachDataFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      dataFilterSelection: dataSelection
    }));
  }

  private handleOutputFormatChange = (outputFormat: OutreachExportFormatType) => {
    this.setState((prevState) => ({
      ...prevState,
      outputFormat: outputFormat
    }));
  }

  private handleSelectQuarterChange = (selectedQuarter: string) => {
    this.setState((prevState) => ({
      ...prevState,
      selectedQuarter: selectedQuarter
    }));
  }

  private handleGenerateClick = async () => {
    this.props.onGenerateClick();
    this.setState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));

    await this.generateNetworkOutreachExport();
    toast.toastInfo(this.state.reportName, this.props.onToastLeave);
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private generateNetworkOutreachExport = async () => {
    const networkOutreachReportModel: ExportNetworkOutreachModel = {
      dataFilter: this.state.dataFilterSelection,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      runAsynchronous: true,
      selectedQuarter: this.state.selectedQuarter
    };
    try {
      switch (this.state.activeExportType) {
        case OutreachExportType.PractitionerAtLocation:
          await this.exportService.postExportPractitionerAtLocationReport(networkOutreachReportModel);
          break;
        case OutreachExportType.Practitioner:
          await this.exportService.postExportPractitionerReport(networkOutreachReportModel);
          break;
        case OutreachExportType.ServiceLocation:
          await this.exportService.postExportServiceLocationReport(networkOutreachReportModel);
          break;
        default: return;
      }
    } catch {
      this.setState({
        error: "An error happened while generating report"
      });
    }
  }
  private formatReportName = (exportType: string) => {
    return exportType
      .split("-")
      .filter(x => x.length > 0)
      .map((x) => (x.charAt(0).toUpperCase() + x.slice(1)))
      .join("");
  }

  private calculateQuarters() {
    const minimumQuarter = history.state.state.minimumQuarter;
    const defaults: Array<{ key: string, value: string }> = [{ key: "90-Day", value: "90-Day" }];
    const currentDate = new Date();
    let currentQuarter = Math.floor(currentDate.getMonth() / 3 + 1);
    let currentYear = currentDate.getFullYear();

    //minimumQuarter format: 2023Q2
    const minQuarterPart = minimumQuarter ? Number(minimumQuarter.substring(minimumQuarter.length - 1)) : currentQuarter;
    const minYear = minimumQuarter ? Number(minimumQuarter.substring(0, 4)) : currentYear;

    let quarterRange = 4;

    if (minYear === currentYear)
      currentQuarter == minQuarterPart ? quarterRange = 1 : quarterRange = currentQuarter - minQuarterPart + 1;
    else if (currentYear - minYear === 1)
      quarterRange = Math.min(currentQuarter - minQuarterPart + 5, 4);

    for (let i = 0; i < quarterRange; i++) {
      defaults.push({
        key: `${currentYear}Q${currentQuarter}`,
        value: `Q${currentQuarter} ${currentYear}`
      });
      currentQuarter = currentQuarter - 1;
      if (currentQuarter == 0) {
        currentQuarter = 4;
        currentYear = currentYear - 1;
      }
    }

    return defaults;
  }
}

const WrappedOutreachDataPanelBody = withFeatureFlagContext(OutreachDataPanelBody);

export default WrappedOutreachDataPanelBody;
